import { useState, useRef, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Building2,
  UserCircle,
  Users,
  LogOut,
  Menu,
  X,
  Rocket,
  Globe,
  Drama,
  Package,
  Search,
  BarChart2,
  ArrowLeftRight,
  Layers,
  ShieldCheck,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { APP_NAME, APP_VERSION, APP_ENVIRONMENT, MENU_STRUCTURE } from '@/constants'
import { useAppStore } from '@/store/appStore'
import type { SectionKey } from '@/store/appStore'
import { ProductosPage } from '@/pages/ProductosPage'
import { UsuariosPage } from '@/pages/UsuariosPage'
import { RolesPage } from '@/pages/RolesPage'
import { PlataformaPage } from '@/pages/PlataformaPage'
import { CatalogoPage } from '@/pages/CatalogoPage'
import { AuditoriaPage } from '@/pages/AuditoriaPage'

// ─── Icon registry ────────────────────────────────────────────────────────────

const ICONS: Record<string, React.ReactNode> = {
  Package:       <Package size={18} />,
  Rocket:        <Rocket size={18} />,
  Globe:         <Globe size={18} />,
  Drama:         <Drama size={18} />,
  Search:        <Search size={18} />,
  UserCircle:    <UserCircle size={18} />,
  Users:         <Users size={18} />,
  BarChart2:     <BarChart2 size={18} />,
  ArrowLeftRight:<ArrowLeftRight size={18} />,
  Layers:        <Layers size={18} />,
  ShieldCheck:   <ShieldCheck size={18} />,
}

// ─── Role badge colors ────────────────────────────────────────────────────────

const ROLE_COLORS: Record<string, string> = {
  ADMIN:                  'bg-red-100 text-red-700 border-red-200',
  KASHIO_SUPER_ADMIN:     'bg-purple-100 text-purple-700 border-purple-200',
  KASHIO_ADMIN:           'bg-violet-100 text-violet-700 border-violet-200',
  SYSTEM_ADMIN:           'bg-orange-100 text-orange-700 border-orange-200',
  ACCOUNT_MANAGER:        'bg-blue-100 text-blue-700 border-blue-200',
  ACCOUNT_CONSULTANT:     'bg-sky-100 text-sky-700 border-sky-200',
  COUNTRY_MANAGER:        'bg-teal-100 text-teal-700 border-teal-200',
  SALES_MANAGER:          'bg-amber-100 text-amber-700 border-amber-200',
  CUSTOMER_ADMINISTRATOR: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  USER:                   'bg-green-100 text-green-700 border-green-200',
  READER:                 'bg-gray-100 text-gray-600 border-gray-200',
}

function roleBadgeClass(role: string): string {
  return ROLE_COLORS[role] ?? 'bg-gray-100 text-gray-600 border-gray-200'
}

function formatRoleName(role: string): string {
  return role
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// ─── Active section renderer ──────────────────────────────────────────────────
// Renders the correct page component based on the store's activeSection.
// Super-only sections fall back to Productos for non-super users.

const SUPER_ONLY_SECTIONS: SectionKey[] = [
  'plataforma_onboarding',
  'plataforma_orgs',
  'catalogo_roles',
  'catalogo_assets',
  'auditoria',
]

function ActiveSectionContent() {
  const { navigation, identity } = useAppStore()
  const { activeSection } = navigation

  if (!identity.isKashioSuper && SUPER_ONLY_SECTIONS.includes(activeSection)) {
    return <ProductosPage />
  }

  switch (activeSection) {
    case 'usuarios':              return <UsuariosPage />
    case 'roles':                 return <RolesPage />
    case 'plataforma_onboarding': return <PlataformaPage variant="onboarding" />
    case 'plataforma_orgs':       return <PlataformaPage variant="orgs" />
    case 'catalogo_roles':        return <CatalogoPage variant="roles" />
    case 'catalogo_assets':       return <CatalogoPage variant="assets" />
    case 'auditoria':             return <AuditoriaPage />
    default:                      return <ProductosPage />
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { user, logout } = useAuth0()
  const { access, identity, roles, navigation, setActiveSection } = useAppStore()

  const userRoles = Object.keys(roles.map)

  const handleLogout = () => {
    useAppStore.getState().reset()
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const visibleSections = MENU_STRUCTURE.map((section) => {
    const visibleItems = section.items.filter(
      (item) =>
        !item.disabled &&
        item.type === 'PAGE' &&
        (item.alwaysVisible || access.map[item.tag]) &&
        (!item.superOnly || identity.isKashioSuper),
    )
    return { ...section, items: visibleItems }
  }).filter(
    (section) =>
      section.items.length > 0 &&
      (!section.superOnly || identity.isKashioSuper),
  )

  const userInitials = user?.name
    ? user.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? '?'

  const primaryRole = userRoles[0]

  return (
    <div className="min-h-screen flex w-full bg-bg-secondary">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-bg-overlay/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex w-[312px] flex-col bg-bg-primary border-r border-border-secondary',
          'transition-transform duration-300 ease-in-out',
          'lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Logo */}
        <div className="h-[72px] flex items-center px-5 border-b border-border-secondary shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-bg-brand-solid shrink-0">
              <Building2 size={16} className="text-fg-white" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-display font-bold text-lg leading-tight truncate">
                <span style={{ color: 'var(--color-brand-dark-950)' }}>Kash</span>
                <span className="text-brand-600">io</span>
              </span>
              <span className="text-[10px] text-text-tertiary font-medium">{APP_NAME}</span>
              <span className="text-[10px] text-text-quaternary">v{APP_VERSION}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                APP_ENVIRONMENT === 'PROD'
                  ? 'bg-green-100 text-green-700'
                  : APP_ENVIRONMENT === 'STG'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-blue-100 text-blue-700',
              )}
            >
              {APP_ENVIRONMENT}
            </span>
            <button
              className="lg:hidden text-fg-tertiary hover:text-fg-primary p-1"
              onClick={() => setSidebarOpen(false)}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-4">
          {visibleSections.map((section) => (
            <div key={section.section} className="mb-2">
              <h3 className="px-3 pt-4 pb-2 font-display uppercase text-[12px] font-bold text-text-tertiary">
                {section.section}
              </h3>
              <ul className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = navigation.activeSection === item.sectionKey
                  return (
                    <li key={item.tag} className="relative px-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (item.sectionKey) setActiveSection(item.sectionKey)
                          setSidebarOpen(false)
                        }}
                        className={cn(
                          'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200',
                          isActive
                            ? 'bg-brand-50 text-brand-700 font-semibold border-l-2 border-brand-600'
                            : 'text-text-tertiary font-medium hover:bg-bg-primary-hover hover:text-text-primary',
                        )}
                      >
                        <span className={cn('shrink-0', isActive ? 'text-brand-600' : 'text-nav-item-icon-fg')}>
                          {ICONS[item.icon] ?? <Building2 size={18} />}
                        </span>
                        <span className="flex-1 line-clamp-1 text-left">{item.name}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Bottom section: Roles + User */}
        <div className="border-t border-border-secondary px-3 py-4 shrink-0 flex flex-col gap-3">

          {userRoles.length > 0 && (
            <div className="px-3">
              <div className="flex items-center gap-1.5 mb-2">
                <ShieldCheck size={13} className="text-text-tertiary shrink-0" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-text-tertiary">
                  Mis Roles
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {userRoles.map((role) => (
                  <span
                    key={role}
                    className={cn(
                      'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide',
                      roleBadgeClass(role),
                    )}
                  >
                    {formatRoleName(role)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {user && (
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setUserMenuOpen((prev) => !prev)}
                className="flex w-full items-center gap-3 px-3 py-2 rounded-lg hover:bg-bg-primary-hover transition-colors duration-200 group"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name ?? 'Avatar'}
                    className="h-8 w-8 rounded-full object-cover border border-border-secondary shrink-0"
                  />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-white font-bold text-xs shadow-sm shrink-0 group-hover:ring-2 group-hover:ring-brand-500/30 transition-all">
                    {userInitials}
                  </div>
                )}
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-sm font-semibold text-text-primary truncate leading-none mb-0.5">
                    {user.name || user.email}
                  </p>
                  {user.name && (
                    <p className="text-xs text-text-tertiary truncate">{user.email}</p>
                  )}
                </div>
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 rounded-xl border border-border-secondary/50 bg-bg-primary/95 backdrop-blur-sm shadow-xl overflow-hidden z-50 p-2">
                  <div className="px-3 py-3 flex flex-col items-center gap-1 border-b border-border-secondary/30 mb-1">
                    <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 font-bold text-base mb-0.5 border border-brand-100 shadow-inner">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name ?? ''} className="w-full h-full rounded-full object-cover" />
                      ) : userInitials}
                    </div>
                    <p className="text-sm font-bold text-text-primary leading-none">{user.name || user.email}</p>
                    {user.name && <p className="text-[11px] text-text-tertiary">{user.email}</p>}
                    {primaryRole && (
                      <div className="mt-1 px-2 py-0.5 rounded-full bg-brand-50 text-brand-600 text-[10px] font-bold uppercase tracking-wider border border-brand-100/50">
                        {formatRoleName(primaryRole)}
                      </div>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    <div className="h-px bg-border-secondary/50 my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-3 py-2 rounded-xl text-sm text-red-600 hover:bg-red-50/50 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center mr-2 text-red-500">
                        <LogOut size={14} />
                      </div>
                      <span className="font-bold">Salir</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Main area */}
      <div className="flex flex-1 flex-col min-w-0 relative bg-bg-secondary">
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-40">
          <div className="absolute -top-[10%] -right-[10%] w-[40%] h-[40%] rounded-full bg-brand-500/10 blur-[120px]" />
          <div className="absolute top-[20%] -left-[5%] w-[30%] h-[30%] rounded-full bg-brand-600/5 blur-[100px]" />
        </div>

        {/* Top header */}
        <header className="h-[72px] sticky top-0 z-40 w-full shrink-0 border-b border-border-secondary/50 bg-bg-primary/80 backdrop-blur-[16px] shadow-sm flex items-center gap-4 px-6">
          <button
            className="lg:hidden text-fg-tertiary hover:text-fg-primary p-1"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>
          <div className="flex-1" />

          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name ?? 'Avatar'}
              className="h-9 w-9 rounded-full object-cover border border-border-secondary"
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-white font-bold text-sm shadow-sm">
              {userInitials}
            </div>
          )}
        </header>

        <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-8">
          <ActiveSectionContent />
        </main>
      </div>
    </div>
  )
}
